import React, { useEffect, useMemo, useState } from 'react';
import { Edit, useForm } from '@refinedev/antd';
import { Table, Space, Tag, Form, Input, InputNumber, Select, message, Tooltip, Typography, Image } from 'antd';
import { LinkOutlined, SearchOutlined } from '@ant-design/icons';
import { API_BASE_URL, axiosInstance } from '../../providers/dataProvider';
import { resolveCmsMediaUrl } from '../../utils/media-url';

interface GymBranchRecord {
  id: string;
  name: string;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  geofenceRadiusM: number;
  gym: { id: string; name: string };
}

interface ListingRow {
  id: string;
  rawBrand?: string | null;
  rawName: string;
  quantity?: number | null;
  notes?: string | null;
  availability: 'AVAILABLE' | 'LIMITED' | 'OFFSITE';
  equipmentCatalogId?: string | null;
  equipmentCatalog?: {
    id: string;
    name: string;
    brand: string;
    imageUrls?: string[];
  } | null;
}

/** Debounced catalog search for the mapping select. */
const CatalogSelect: React.FC<{
  value?: string | null;
  onChange: (v: string | null) => void;
  selected?: { id: string; name: string; brand?: string } | null;
}> = ({ value, onChange, selected }) => {
  const [options, setOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [search, setSearch] = useState('');

  // Keep the currently mapped machine visible as its NAME (not the raw uuid):
  // merge it into the option list ahead of any search results.
  useEffect(() => {
    if (selected?.id) {
      setOptions((prev) => {
        if (prev.some((o) => o.value === selected.id)) return prev;
        return [
          {
            label: selected.brand ? `${selected.brand} — ${selected.name}` : selected.name,
            value: selected.id,
          },
          ...prev,
        ];
      });
    }
  }, [selected?.id, selected?.name, selected?.brand]);

  useEffect(() => {
    if (!search.trim()) return;
    const timer = setTimeout(() => {
      axiosInstance
        .get(`${API_BASE_URL}/equipment-catalog`, { params: { query: search.trim(), limit: 20 } })
        .then((res) => {
          const items = res.data?.items ?? [];
          setOptions(
            items.map((i: { id: string; name: string; brand?: string }) => ({
              label: i.brand ? `${i.brand} — ${i.name}` : i.name,
              value: i.id,
            })),
          );
        })
        .catch(() => setOptions([]));
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  return (
    <Select
      showSearch
      allowClear
      filterOption={false}
      style={{ minWidth: 320 }}
      placeholder="Search catalog machine to map…"
      value={value ?? null}
      onSearch={setSearch}
      onChange={(v) => onChange(v ?? null)}
      options={options}
      notFoundContent={search.trim() ? 'No catalog matches' : 'Type to search the catalog'}
    />
  );
};

export const GymBranchEdit: React.FC = () => {
  const { formProps, saveButtonProps, queryResult } = useForm<GymBranchRecord>({
    resource: 'gym-branches',
  });
  const branchId = String(queryResult?.data?.data?.id ?? '');
  const [listings, setListings] = useState<ListingRow[]>([]);

  useEffect(() => {
    if (!branchId) return;
    axiosInstance
      .get(`${API_BASE_URL}/gym-branches/${branchId}/equipment`)
      .then((res) => setListings(res.data?.items ?? []))
      .catch(() => setListings([]));
  }, [branchId]);

  const unmappedCount = useMemo(
    () => listings.filter((l) => !l.equipmentCatalogId).length,
    [listings],
  );

  const [searchText, setSearchText] = useState('');
  const filteredListings = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return listings;
    return listings.filter((l) =>
      [l.rawBrand, l.rawName, l.notes, l.equipmentCatalog?.name, l.equipmentCatalog?.brand]
        .some((v) => (v ?? '').toLowerCase().includes(q)),
    );
  }, [listings, searchText]);

  const saveListing = async (row: ListingRow, values: Partial<ListingRow>) => {
    try {
      await axiosInstance.post(`${API_BASE_URL}/gym-branches/${branchId}/equipment`, {
        rawName: row.rawName,
        rawBrand: row.rawBrand ?? undefined,
        equipmentCatalogId: values.equipmentCatalogId ?? row.equipmentCatalogId ?? null,
        availability: values.availability ?? row.availability,
        quantity: values.quantity ?? row.quantity ?? undefined,
        notes: values.notes ?? row.notes ?? undefined,
      });
      // Refetch: a remap changes the catalog name/image the row should display.
      const res = await axiosInstance.get(`${API_BASE_URL}/gym-branches/${branchId}/equipment`);
      setListings(res.data?.items ?? []);
      message.success('Row saved');
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? 'Save failed');
    }
  };

  return (
    <Edit saveButtonProps={saveButtonProps}>
      <Form {...formProps} layout="vertical">
        <Space size={24} wrap>
          <Form.Item name="name" label="Branch name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="address" label="Address">
            <Input />
          </Form.Item>
          <Form.Item name="latitude" label="Latitude">
            <InputNumber step={0.0001} />
          </Form.Item>
          <Form.Item name="longitude" label="Longitude">
            <InputNumber step={0.0001} />
          </Form.Item>
          <Form.Item name="geofenceRadiusM" label="GPS radius (m)">
            <InputNumber min={50} />
          </Form.Item>
          <Form.Item name="isDefault" label="Default branch" valuePropName="checked">
            <Select
              options={[
                { label: 'Default — scans that miss GPS use this list', value: true },
                { label: 'Not default — GPS proximity only', value: false },
              ]}
              style={{ width: 280 }}
            />
          </Form.Item>
        </Space>
        <Typography.Paragraph type="secondary">
          Scans within this radius prioritize this branch's AVAILABLE equipment list.
          A default branch also backs scans that resolve to no branch at all.
        </Typography.Paragraph>
      </Form>

      <Typography.Title level={5} style={{ marginTop: 24 }}>
        Branch equipment sheet {unmappedCount > 0 && <Tag color="orange">{unmappedCount} unmapped</Tag>}
      </Typography.Title>
      <Input
        allowClear
        prefix={<SearchOutlined />}
        placeholder="Search brand, sheet name, mapped machine or notes…"
        style={{ maxWidth: 420, marginBottom: 12 }}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
      />
      <Table dataSource={filteredListings} rowKey="id" size="small" pagination={{ pageSize: 25 }} scroll={{ x: 1300 }}>
        <Table.Column
          title="Image"
          width={90}
          render={(_: any, row: ListingRow) => {
            const url = resolveCmsMediaUrl(row.equipmentCatalog?.imageUrls?.[0]);
            return url ? <Image src={url} width={64} /> : '—';
          }}
        />
        <Table.Column dataIndex="rawBrand" title="Brand" width={140} render={(v: string) => v ?? '—'} />
        <Table.Column dataIndex="rawName" title="Sheet name" width={240} />
        <Table.Column
          title="Mapped catalog machine"
          width={430}
          render={(_: any, row: ListingRow) => (
            <Space direction="vertical" size={4}>
              <Space>
                <CatalogSelect
                  value={row.equipmentCatalogId}
                  selected={row.equipmentCatalog}
                  onChange={(catalogId) => saveListing(row, { equipmentCatalogId: catalogId ?? null })}
                />
                {row.equipmentCatalog && (
                  <Tooltip title="Open equipment detail">
                    <a
                      href={`/equipment-catalog/show/${row.equipmentCatalog.id}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <LinkOutlined />
                    </a>
                  </Tooltip>
                )}
              </Space>
              {!row.equipmentCatalogId && (
                <Tag color="orange" style={{ marginInlineEnd: 0 }}>
                  unmapped — excluded from scan priority
                </Tag>
              )}
            </Space>
          )}
        />
        <Table.Column
          dataIndex="availability"
          title="Availability"
          width={170}
          render={(_: any, row: ListingRow) => (
            <Select
              value={row.availability}
              style={{ width: 130 }}
              onChange={(availability) => saveListing(row, { availability })}
              options={['AVAILABLE', 'LIMITED', 'OFFSITE'].map((v) => ({ label: v, value: v }))}
            />
          )}
        />
        <Table.Column dataIndex="quantity" title="Qty" width={70} render={(v: number) => v ?? '—'} />
        <Table.Column dataIndex="notes" title="Notes" render={(v: string) => v ?? '—'} />
      </Table>
    </Edit>
  );
};
